import React from "react";

/**
 * Shared admin surface primitives. These bake in the ONE canonical card look
 * (white surface, slate-200 hairline, rounded-2xl, subtle shadow) so every
 * club/admin page stops hand-rolling its own card classes. Purely presentational
 * — they wrap existing content (tables, forms, filters, modals) unchanged.
 */

/** Bare card surface. Add `padded` for the standard inner padding. */
export function AdminCard({
  children,
  className = "",
  padded = false,
  ...rest
}: { padded?: boolean } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`bg-white border border-slate-200 rounded-2xl shadow-sm ${padded ? "p-5" : ""} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * A card with a standard header bar (title + optional subtitle + right-aligned
 * actions) and a body. Use for lists/tables/sections that today use an ad-hoc
 * `<h3>` inside a div.
 */
export function SectionCard({
  title,
  subtitle,
  actions,
  children,
  className = "",
  bodyClassName = "",
  bodyPadded = false,
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
  bodyPadded?: boolean;
}) {
  return (
    <AdminCard className={className}>
      {(title || actions) && (
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
          <div className="min-w-0">
            {title && <h2 className="text-[15px] font-bold text-slate-800 leading-tight">{title}</h2>}
            {subtitle && <p className="text-xs text-slate-500 font-medium mt-0.5">{subtitle}</p>}
          </div>
          {actions && <div className="ml-auto flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
      )}
      <div className={`${bodyPadded ? "p-5" : ""} ${bodyClassName}`}>{children}</div>
    </AdminCard>
  );
}

type Tone = "up" | "down" | "muted";
const TONE: Record<Tone, string> = {
  up: "text-emerald-600",
  down: "text-rose-600",
  muted: "text-slate-400",
};

/**
 * A summary stat tile: a small labelled headline number with an optional accent
 * dot and a delta/hint line. Standardises the ad-hoc stat tiles scattered across
 * Overview / Athletes / Quizzes / Affiliate.
 */
export function StatCard({
  label,
  value,
  hint,
  hintTone = "muted",
  dotClass,
  valueClass = "text-slate-800",
  icon,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  hintTone?: Tone;
  /** Tailwind bg-* class for the accent dot, e.g. "bg-indigo-500". */
  dotClass?: string;
  /** Override the value color, e.g. "text-emerald-600". */
  valueClass?: string;
  icon?: React.ReactNode;
}) {
  return (
    <AdminCard className="p-4">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-500">
        {dotClass && <span className={`w-2 h-2 rounded-[3px] ${dotClass}`} />}
        {icon}
        <span className="truncate">{label}</span>
      </div>
      <div className={`text-[26px] leading-tight font-extrabold mt-1.5 tabular-nums ${valueClass}`}>{value}</div>
      {hint && <div className={`text-xs font-semibold mt-1 ${TONE[hintTone]}`}>{hint}</div>}
    </AdminCard>
  );
}

/** Responsive grid for a row of StatCards (2 cols on mobile, N on desktop). */
export function StatGrid({
  children,
  cols = 4,
  className = "",
}: {
  children: React.ReactNode;
  cols?: 2 | 3 | 4;
  className?: string;
}) {
  const map = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4" } as const;
  return <div className={`grid grid-cols-2 gap-3 ${map[cols]} ${className}`}>{children}</div>;
}
